import { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Mail, CheckCircle, Pencil, Trash2, Eye, AlertTriangle, Clock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function MailConfigs({ user }) {
  const [configs, setConfigs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingConfig, setViewingConfig] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingConfig, setDeletingConfig] = useState(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testingExistingConfig, setTestingExistingConfig] = useState(null);
  const [existingTestRecipient, setExistingTestRecipient] = useState('');
  const [testingExisting, setTestingExisting] = useState(false);
  const [provider, setProvider] = useState('smtp');
  const [testRecipient, setTestRecipient] = useState('');
  const [testingConfig, setTestingConfig] = useState(false);
  const getInitialFormData = () => ({
    company_id: user?.role === 'superadmin' ? '' : user?.company_id || '',
    name: '', from_email: '', from_name: '', provider: 'smtp',
    smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '', use_tls: true,
    tenant_id: '', client_id: '', client_secret: '', sender_obj_id: '', client_secret_expire: '',
    secret_expiry_reminder_email: '', secret_expiry_reminder_days: 5,
    sendgrid_api_key: ''
  });
  const [formData, setFormData] = useState({
    company_id: user?.role === 'superadmin' ? '' : user?.company_id || '',
    name: '', from_email: '', from_name: '', provider: 'smtp',
    smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '', use_tls: true,
    tenant_id: '', client_id: '', client_secret: '', sender_obj_id: '', client_secret_expire: '',
    secret_expiry_reminder_email: '', secret_expiry_reminder_days: 5,
    sendgrid_api_key: ''
  });

  useEffect(() => {
    loadConfigs();
    loadCompanyInfo();
    if (user?.role === 'superadmin') {
      api.get('/companies').then(res => setCompanies(res.data));
    }
  }, [user]);

  const loadCompanyInfo = async () => {
    if (user?.company_id) {
      try {
        const res = await api.get(`/companies/${user.company_id}`);
        setCurrentCompany(res.data);
      } catch (error) {
        console.error('Failed to load company info');
      }
    }
  };

  const loadConfigs = async () => {
    const configsRes = await api.get('/mail-configs');
    
    // If superadmin, fetch company names
    if (user?.role === 'superadmin') {
      const companiesRes = await api.get('/companies');
      const companyMap = {};
      companiesRes.data.forEach(c => companyMap[c.id] = c.name);
      
      const enrichedConfigs = configsRes.data.map(config => ({
        ...config,
        company_name: companyMap[config.company_id] || 'Unknown'
      }));
      setConfigs(enrichedConfigs);
    } else {
      setConfigs(configsRes.data);
    }
  };

  const resetForm = () => {
    setFormData(getInitialFormData());
    setProvider('smtp');
    setTestRecipient('');
    setTestingConfig(false);
    setEditMode(false);
    setEditingConfig(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (user?.role === 'superadmin' && !formData.company_id) {
        toast.error('Select a company for this mail config');
        return;
      }
      if (editMode) {
        await api.put(`/mail-configs/${editingConfig.id}`, buildConfigPayload());
        toast.success('Mail config updated');
      } else {
        await api.post('/mail-configs', buildConfigPayload());
        toast.success('Mail config created');
      }
      setOpen(false);
      resetForm();
      loadConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editMode ? 'Failed to update config' : 'Failed to create config'));
    }
  };

  const buildConfigPayload = () => ({
    ...formData,
    provider,
    secret_expiry_reminder_email: formData.secret_expiry_reminder_email || null,
  });

  const handleDraftTest = async () => {
    if (!testRecipient) {
      toast.error('Enter a receiver email for the test');
      return;
    }
    if (user?.role === 'superadmin' && !formData.company_id) {
      toast.error('Select a company before testing this config');
      return;
    }

    setTestingConfig(true);
    try {
      await api.post('/mail-configs/test-draft', {
        ...buildConfigPayload(),
        recipient_email: testRecipient
      });
      toast.success('Test email sent');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'object'
        ? `${detail.provider || 'Provider'} failed at ${detail.stage || 'send'}: ${detail.error || 'Unknown error'}`
        : detail || 'Test email failed';
      toast.error(message);
    } finally {
      setTestingConfig(false);
    }
  };

  const handleEdit = (config) => {
    setEditMode(true);
    setEditingConfig(config);
    setProvider(config.provider);
    setFormData({
      company_id: config.company_id || '',
      name: config.name,
      from_email: config.from_email,
      from_name: config.from_name,
      provider: config.provider,
      smtp_host: config.smtp_host || '',
      smtp_port: config.smtp_port || 587,
      smtp_username: config.smtp_username || '',
      smtp_password: config.smtp_password || '',
      use_tls: config.use_tls ?? true,
      tenant_id: config.tenant_id || '',
      client_id: config.client_id || '',
      client_secret: '',
      sender_obj_id: config.sender_obj_id || '',
      client_secret_expire: config.client_secret_expire ? new Date(config.client_secret_expire).toISOString().split('T')[0] : '',
      secret_expiry_reminder_email: config.secret_expiry_reminder_email || '',
      secret_expiry_reminder_days: config.secret_expiry_reminder_days || 5,
      sendgrid_api_key: ''
    });
    setOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingConfig) return;
    try {
      await api.delete(`/mail-configs/${deletingConfig.id}`);
      toast.success('Mail config deleted');
      setDeleteDialogOpen(false);
      setDeletingConfig(null);
      loadConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete config');
    }
  };

  const openTestDialog = (config) => {
    setTestingExistingConfig(config);
    setExistingTestRecipient('');
    setTestDialogOpen(true);
  };

  const testConfig = async () => {
    if (!testingExistingConfig || !existingTestRecipient) return;
    setTestingExisting(true);
    try {
      const res = await api.post(`/mail-configs/${testingExistingConfig.id}/test`, { recipient_email: existingTestRecipient });
      toast.success(res.data.message);
      setTestDialogOpen(false);
      setTestingExistingConfig(null);
      setExistingTestRecipient('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'object'
        ? `${detail.provider || 'Provider'} failed at ${detail.stage || 'send'}: ${detail.error || 'Unknown error'}`
        : detail || 'Test failed';
      toast.error(message);
    } finally {
      setTestingExisting(false);
    }
  };

  const getExpiryStatus = (config) => {
    if (config.provider !== 'azure' || !config.client_secret_expire) {
      return null;
    }

    const expireDate = new Date(config.client_secret_expire);
    const now = new Date();
    const daysUntilExpiry = Math.floor((expireDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { status: 'expired', color: 'text-red-600', bgColor: 'bg-red-50', icon: AlertTriangle, message: 'Expired' };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expiring-soon', color: 'text-orange-600', bgColor: 'bg-orange-50', icon: AlertTriangle, message: `Expires in ${daysUntilExpiry}d` };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'warning', color: 'text-yellow-600', bgColor: 'bg-yellow-50', icon: Clock, message: `Expires in ${daysUntilExpiry}d` };
    }
    
    return { status: 'valid', color: 'text-green-600', bgColor: 'bg-green-50', icon: CheckCircle, message: `Valid (${daysUntilExpiry}d)` };
  };

  return (
    <div className="space-y-6" data-testid="mail-configs-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Mail Configurations</h1>
        <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-config-button"><Plus className="w-4 h-4 mr-2" />New Config</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit' : 'Create'} Mail Configuration</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              {user?.role === 'superadmin' && (
                <div>
                  <Label>Company</Label>
                  <Select
                    value={formData.company_id}
                    onValueChange={(value) => setFormData({...formData, company_id: value})}
                  >
                    <SelectTrigger data-testid="config-company-select">
                      <SelectValue placeholder="Select company..." />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(company => (
                        <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Config Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="config-name-input" />
              </div>
              <div>
                <Label>Provider</Label>
                <Select value={provider} onValueChange={setProvider} disabled={editMode}>
                  <SelectTrigger data-testid="provider-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="smtp">SMTP</SelectItem>
                    <SelectItem value="azure">Azure Graph API</SelectItem>
                    <SelectItem value="sendgrid">SendGrid API</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>From Email</Label>
                  <Input type="email" value={formData.from_email} onChange={(e) => setFormData({...formData, from_email: e.target.value})} required data-testid="from-email-input" />
                </div>
                <div>
                  <Label>From Name</Label>
                  <Input value={formData.from_name} onChange={(e) => setFormData({...formData, from_name: e.target.value})} required data-testid="from-name-input" />
                </div>
              </div>
              
              {provider === 'smtp' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>SMTP Host</Label>
                      <Input value={formData.smtp_host} onChange={(e) => setFormData({...formData, smtp_host: e.target.value})} required data-testid="smtp-host-input" />
                    </div>
                    <div>
                      <Label>SMTP Port</Label>
                      <Input type="number" value={formData.smtp_port} onChange={(e) => setFormData({...formData, smtp_port: parseInt(e.target.value)})} required data-testid="smtp-port-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Username</Label>
                      <Input value={formData.smtp_username} onChange={(e) => setFormData({...formData, smtp_username: e.target.value})} data-testid="smtp-username-input" />
                    </div>
                    <div>
                      <Label>Password {editMode && '(leave blank to keep current)'}</Label>
                      <Input type="password" value={formData.smtp_password} onChange={(e) => setFormData({...formData, smtp_password: e.target.value})} data-testid="smtp-password-input" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch checked={formData.use_tls} onCheckedChange={(v) => setFormData({...formData, use_tls: v})} data-testid="use-tls-switch" />
                    <Label>Use TLS</Label>
                  </div>
                </>
              ) : provider === 'azure' ? (
                <>
                  <div>
                    <Label>Tenant ID</Label>
                    <Input value={formData.tenant_id} onChange={(e) => setFormData({...formData, tenant_id: e.target.value})} required data-testid="tenant-id-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Client ID</Label>
                      <Input value={formData.client_id} onChange={(e) => setFormData({...formData, client_id: e.target.value})} required data-testid="client-id-input" />
                    </div>
                    <div>
                      <Label>Client Secret {editMode && '(leave blank to keep current)'}</Label>
                      <Input type="password" value={formData.client_secret} onChange={(e) => setFormData({...formData, client_secret: e.target.value})} required={!editMode} data-testid="client-secret-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Sender Object ID</Label>
                      <Input value={formData.sender_obj_id} onChange={(e) => setFormData({...formData, sender_obj_id: e.target.value})} data-testid="sender-obj-id-input" />
                    </div>
                    <div>
                      <Label>Client Secret Expiry</Label>
                      <Input type="date" value={formData.client_secret_expire} onChange={(e) => setFormData({...formData, client_secret_expire: e.target.value})} data-testid="secret-expire-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Expiry Reminder Email</Label>
                      <Input type="email" value={formData.secret_expiry_reminder_email} onChange={(e) => setFormData({...formData, secret_expiry_reminder_email: e.target.value})} placeholder="ops@example.com" data-testid="secret-reminder-email-input" />
                    </div>
                    <div>
                      <Label>Reminder Window Days</Label>
                      <Input type="number" min="1" max="30" value={formData.secret_expiry_reminder_days} onChange={(e) => setFormData({...formData, secret_expiry_reminder_days: parseInt(e.target.value || '5', 10)})} data-testid="secret-reminder-days-input" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label>SendGrid API Key {editMode && '(leave blank to keep current)'}</Label>
                    <Input
                      type="password"
                      value={formData.sendgrid_api_key}
                      onChange={(e) => setFormData({...formData, sendgrid_api_key: e.target.value})}
                      required={!editMode}
                      data-testid="sendgrid-api-key-input"
                    />
                  </div>
                </>
              )}
              <div className="rounded-lg border p-3 space-y-3">
                <Label>Test Receiver Email</Label>
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <Input
                    type="email"
                    value={testRecipient}
                    onChange={(e) => setTestRecipient(e.target.value)}
                    placeholder="receiver@example.com"
                    data-testid="test-recipient-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDraftTest}
                    disabled={testingConfig}
                    data-testid="test-draft-config-button"
                  >
                    {testingConfig ? 'Testing...' : 'Send Test'}
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-config-button">{editMode ? 'Update' : 'Create'} Configuration</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Configuration Details</DialogTitle>
          </DialogHeader>
          {viewingConfig && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Name</Label>
                  <div className="font-medium">{viewingConfig.name}</div>
                </div>
                <div>
                  <Label className="text-gray-600">Provider</Label>
                  <div className="font-medium uppercase">{viewingConfig.provider}</div>
                </div>
              </div>
              <div>
                <Label className="text-gray-600">API Config GUID</Label>
                <div className="rounded bg-gray-50 p-2 font-mono text-sm break-all">{viewingConfig.guid}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">From Email</Label>
                  <div className="font-medium">{viewingConfig.from_email}</div>
                </div>
                <div>
                  <Label className="text-gray-600">From Name</Label>
                  <div className="font-medium">{viewingConfig.from_name}</div>
                </div>
              </div>
              {viewingConfig.provider === 'smtp' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-600">Host</Label>
                      <div className="font-medium">{viewingConfig.smtp_host}</div>
                    </div>
                    <div>
                      <Label className="text-gray-600">Port</Label>
                      <div className="font-medium">{viewingConfig.smtp_port}</div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-600">TLS</Label>
                    <div className="font-medium">{viewingConfig.use_tls ? 'Enabled' : 'Disabled'}</div>
                  </div>
                </>
              ) : viewingConfig.provider === 'azure' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-600">Tenant ID</Label>
                      <div className="font-medium font-mono text-sm">{viewingConfig.tenant_id}</div>
                    </div>
                    <div>
                      <Label className="text-gray-600">Client ID</Label>
                      <div className="font-medium font-mono text-sm">{viewingConfig.client_id}</div>
                    </div>
                  </div>
                  {viewingConfig.client_secret_expire && (
                    <div>
                      <Label className="text-gray-600">Client Secret Expires</Label>
                      <div className="font-medium">{new Date(viewingConfig.client_secret_expire).toLocaleDateString()}</div>
                    </div>
                  )}
                  {viewingConfig.secret_expiry_reminder_email && (
                    <div>
                      <Label className="text-gray-600">Reminder Contact</Label>
                      <div className="font-medium">{viewingConfig.secret_expiry_reminder_email}</div>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <Label className="text-gray-600">API Key</Label>
                  <div className="font-medium">Stored securely</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Mail Configuration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingConfig?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Test Mail Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-sm font-medium">{testingExistingConfig?.name}</div>
              <div className="text-xs text-gray-500">{testingExistingConfig?.provider?.toUpperCase()} · {testingExistingConfig?.from_email}</div>
            </div>
            <div>
              <Label>Receiver Email</Label>
              <Input
                type="email"
                value={existingTestRecipient}
                onChange={(e) => setExistingTestRecipient(e.target.value)}
                placeholder="receiver@example.com"
                data-testid="existing-test-recipient-input"
              />
            </div>
            <Button type="button" className="w-full" onClick={testConfig} disabled={testingExisting || !existingTestRecipient} data-testid="send-existing-test-button">
              {testingExisting ? 'Testing...' : 'Send Test'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Company Association Banner */}
      {currentCompany && (
        <Card className="glass-card border-2 border-blue-200 bg-blue-50/50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">Company: {currentCompany.name}</h3>
                <p className="text-sm text-gray-600">
                  {user?.role === 'superadmin' ? 'Select a company when creating or editing a mail configuration.' : 'All mail configurations are automatically linked to your company. You can only see and manage configurations for your company.'}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{configs.length}</div>
                <div className="text-sm text-gray-600">Configurations</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {configs.map(config => {
          const expiryStatus = getExpiryStatus(config);
          const ExpiryIcon = expiryStatus?.icon;
          
          return (
            <Card key={config.id} className="glass-card" data-testid={`config-card-${config.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    {config.name}
                  </CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" data-testid={`config-actions-${config.id}`}>
                        Actions
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setViewingConfig(config); setViewDialogOpen(true); }} data-testid={`view-config-${config.id}`}>
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEdit(config)} data-testid={`edit-config-${config.id}`}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openTestDialog(config)} data-testid={`test-config-${config.id}`}>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Test Connection
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => { setDeletingConfig(config); setDeleteDialogOpen(true); }} 
                        className="text-red-600"
                        data-testid={`delete-config-${config.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {user?.role === 'superadmin' && config.company_name && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Company:</span>
                    <span className="font-medium">{config.company_name}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Provider:</span>
                  <span className="font-medium uppercase">{config.provider}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">From:</span>
                  <span className="font-medium">{config.from_email}</span>
                </div>
                <div className="text-xs text-gray-500">
                  API GUID: <code className="break-all">{config.guid}</code>
                </div>
                {expiryStatus && (
                  <div className={`flex items-center gap-2 p-2 rounded-lg ${expiryStatus.bgColor}`}>
                    <ExpiryIcon className={`w-4 h-4 ${expiryStatus.color}`} />
                    <span className={`text-sm font-medium ${expiryStatus.color}`}>
                      {expiryStatus.message}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
